import { Anchor, Group, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useIsDetailView } from './detail/DetailViewContext';
import { shortId } from '../lib/labels';
import { CopyButton } from './CopyButton';

export interface EntityRefProps {
    title: string;
    id?: string | null;
    secondary?: string | null;
    href?: string;
    /** Hide monospace id line when title alone is enough */
    hideId?: boolean;
    /** Show copy buttons (defaults to true only inside detail drawers) */
    copyable?: boolean;
}

const COPY_LABEL_KEYS: Record<string, string> = {
    name: 'common.copy.name',
    detail: 'common.copy.detail',
    ID: 'common.copy.id',
};

function RefLine({
    children,
    copyValue,
    copyLabel,
    copyable,
}: {
    children: ReactNode;
    copyValue?: string | null;
    copyLabel?: string;
    copyable: boolean;
}) {
    const { t } = useTranslation();

    if (!copyable || !copyValue?.trim()) {
        return <>{children}</>;
    }

    const label = copyLabel
        ? t(COPY_LABEL_KEYS[copyLabel] ?? copyLabel)
        : undefined;

    return (
        <Group gap={4} wrap="nowrap" align="center">
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
            <CopyButton value={copyValue} label={label} />
        </Group>
    );
}

export function EntityRef({
    title,
    id,
    secondary,
    href,
    hideId,
    copyable: copyableProp,
}: EntityRefProps) {
    const inDetailView = useIsDetailView();
    const copyable = copyableProp ?? inDetailView;
    const idLine = id && !hideId ? shortId(id) : null;

    const titleNode = href ? (
        <Anchor component={Link} to={href} size="sm" fw={600} lineClamp={1}>
            {title}
        </Anchor>
    ) : (
        <Text size="sm" fw={600} lineClamp={1}>
            {title}
        </Text>
    );

    return (
        <Stack gap={2}>
            <RefLine copyable={copyable} copyValue={title} copyLabel="name">
                {titleNode}
            </RefLine>
            {secondary ? (
                <RefLine copyable={copyable} copyValue={secondary} copyLabel="detail">
                    <Text size="xs" c="dimmed" lineClamp={1}>
                        {secondary}
                    </Text>
                </RefLine>
            ) : null}
            {idLine && id ? (
                <RefLine copyable={copyable} copyValue={id} copyLabel="ID">
                    <Text size="xs" c="dimmed" ff="monospace">
                        {idLine}
                    </Text>
                </RefLine>
            ) : null}
        </Stack>
    );
}
